import { useState } from 'react';
import { Key, Copy, CheckCircle2, AlertCircle } from 'lucide-react';

export default function JWTDebugger() {
  const [token, setToken] = useState('');
  const [header, setHeader] = useState('');
  const [payload, setPayload] = useState('');
  const [signature, setSignature] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<'header' | 'payload' | 'signature' | null>(null);

  const decodeJWT = (jwt: string) => {
    try {
      setError('');
      if (!jwt.trim()) {
        setHeader('');
        setPayload('');
        setSignature('');
        return;
      }

      const parts = jwt.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format. Must have 3 parts separated by dots.');
      }

      // Decode header
      const decodedHeader = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
      setHeader(JSON.stringify(decodedHeader, null, 2));

      // Decode payload
      const decodedPayload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      setPayload(JSON.stringify(decodedPayload, null, 2));

      // Signature (keep as is)
      setSignature(parts[2]);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JWT token');
      setHeader('');
      setPayload('');
      setSignature('');
    }
  };

  const copyToClipboard = (text: string, section: 'header' | 'payload' | 'signature') => {
    navigator.clipboard.writeText(text);
    setCopied(section);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-xl shadow-lg">
          <Key className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-dark-text">JWT Debugger</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted">Decode and inspect JWT tokens</p>
        </div>
      </div>

      {/* Input */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
          JWT Token
        </label>
        <textarea
          value={token}
          onChange={(e) => {
            setToken(e.target.value);
            decodeJWT(e.target.value);
          }}
          placeholder="Paste your JWT token here (e.g., eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...)"
          className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent resize-none transition-all"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Decoded Sections */}
      {(header || payload || signature) && (
        <div className="grid gap-4">
          {/* Header */}
          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-coral-600 dark:text-coral-400">HEADER</h3>
              <button
                onClick={() => copyToClipboard(header, 'header')}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
                title="Copy header"
              >
                {copied === 'header' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
            <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-bg p-3 rounded overflow-x-auto text-gray-900 dark:text-dark-text">
              {header}
            </pre>
          </div>

          {/* Payload */}
          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-teal-600 dark:text-teal-400">PAYLOAD</h3>
              <button
                onClick={() => copyToClipboard(payload, 'payload')}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
                title="Copy payload"
              >
                {copied === 'payload' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
            <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-bg p-3 rounded overflow-x-auto text-gray-900 dark:text-dark-text">
              {payload}
            </pre>
          </div>

          {/* Signature */}
          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-purple-600 dark:text-purple-400">SIGNATURE</h3>
              <button
                onClick={() => copyToClipboard(signature, 'signature')}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
                title="Copy signature"
              >
                {copied === 'signature' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
            <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-bg p-3 rounded overflow-x-auto break-all text-gray-900 dark:text-dark-text">
              {signature}
            </pre>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!token && (
        <div className="text-center py-12 text-gray-500 dark:text-dark-muted">
          <Key className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-2">No JWT Token</p>
          <p className="text-sm">Paste a JWT token above to decode and inspect it</p>
        </div>
      )}
    </div>
  );
}
